import { useEffect, useMemo, useState } from "react";
import DaySelector from "./DaySelector";
import TimeSelector from "./TimeSelector";
import { Select, Label, Input } from "@woodmac-hycb/form-input"
import { Col, View } from "@woodmac-hycb/layout";
import { Text } from "@woodmac-hycb/text";
import { Row } from '@woodmac-hycb/layout';
import BoundaryAlert from "./BoundaryAlert";
import OutputChangeAlert from "./OutputChangeAlert";
import { createAlert, updateAlert } from "../../services/AlertService";
import { useToggleRegion } from "../../hooks/useToggleRegion";
import '../CustomAlertManager.css';

function AlertDefinition({ selectedPlant, addNewAlert, selectedAlert }) {

    const alertTypes = ['Boundary', 'Output Change'];
    const changeDirections = ['Up', 'Either', 'Down'];

    const { isRegionNa } = useToggleRegion();

    const newBoundaryA = useMemo(() => ({ upwardBoundary: '', downwardBoundary: '' }), []);
    const newOutputChangeA = { changeType: 'Megawatt', sampleType: 'Mean', change: null, hoursToSample: null, ignoreAbove: null, ignoreBelow: null };

    const newAlert = useMemo(() => ({
        boundaryAlert: newBoundaryA,
        outputChangeAlert: null,
        title: null,
        entityId: null,
        typeId: 100,
        daySu: 0,
        dayM: 0,
        dayT: 0,
        dayW: 0,
        dayTh: 0,
        dayF: 0,
        dayS: 0,
        startHour: '00',
        startMin: '00',
        endHour: '23',
        endMin: '59',
        sensitivity: 5,
        directionOfChange: "Up",
        isGenscapeAlert: true,
        isActive: true
    }), [newBoundaryA]);

    const [alert, setAlert] = useState(newAlert);
    const [duplicateTitle, setDuplicateTitle] = useState(false);

    useEffect(() => {
        setAlert({ ...newAlert, entityId: selectedPlant ? selectedPlant.entityId : null });
    }, [selectedPlant, newAlert])

    useEffect(() => {
        if (selectedAlert) {
            setAlert({ ...selectedAlert });
        } else {
            setAlert({ ...newAlert, entityId: selectedPlant ? selectedPlant.entityId : null });
        }
    }, [selectedAlert, newAlert, selectedPlant])

    const setStart = (startTime) => {
        const time = startTime ? startTime.split(':') : ['00', '00'];
        setAlert(a => ({ ...a, startHour: time[0], startMin: time[1] }));
    };

    const setEnd = (endTime) => {
        const time = endTime ? endTime.split(':') : ['00', '00'];
        setAlert(a => ({ ...a, endHour: time[0], endMin: time[1] }));
    };

    const setBoundaries = (boundary, dir) => {
        if (dir === 'up') {
            setAlert(a => ({ ...alert, boundaryAlert: { ...a.boundaryAlert, upwardBoundary: boundary } }));
        } else if (dir === 'down') {
            setAlert(a => ({ ...alert, boundaryAlert: { ...a.boundaryAlert, downwardBoundary: boundary } }));
        }
    }

    const setChange = (change) => {
        setAlert(a => ({ ...a, outputChangeAlert: { ...a.outputChangeAlert, change: change } }));
    }

    const setChangeType = (changeType) => {
        setAlert(a => ({ ...a, outputChangeAlert: { ...a.outputChangeAlert, changeType: changeType } }));
    }

    const setSampleType = (sampleType) => {
        setAlert(a => ({ ...a, outputChangeAlert: { ...a.outputChangeAlert, sampleType: sampleType } }));
    }

    const setSampleHours = (hours) => {
        setAlert(a => ({ ...a, outputChangeAlert: { ...a.outputChangeAlert, hoursToSample: hours } }));
    }

    const setIgnore = (hours, dir) => {
        if (dir === 'up') {
            setAlert(a => ({ ...a, outputChangeAlert: { ...a.outputChangeAlert, ignoreAbove: hours } }));
        } else if (dir === 'down') {
            setAlert(a => ({ ...a, outputChangeAlert: { ...a.outputChangeAlert, ignoreBelow: hours } }));
        }
    }

    const switchAlertType = (e) => {
        setAlert({
            ...alert,
            typeId: e.target.value === alertTypes[0] ? 100 : 200,
            outputChangeAlert: e.target.value === alertTypes[0] ? null : newOutputChangeA,
            boundaryAlert: e.target.value === alertTypes[0] ? newBoundaryA : null
        });
    }

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (selectedPlant) {
            if (alert.id) {
                try {
                    const token = "";
                    await updateAlert(alert.id, alert, isRegionNa ? "na" : "eu", token);
                    addNewAlert(alert);
                    setDuplicateTitle(false);
                } catch (error) {
                    if (error.response.status === 409) {
                        setDuplicateTitle(true);
                    }
                }
            } else {
                try {
                    const token = "";
                    const response = await createAlert(alert, isRegionNa ? "na" : "eu", token);
                    var alertToAdd = response.data;
                    addNewAlert(alertToAdd);
                    setDuplicateTitle(false);
                    setAlert({ ...newAlert, entityId: selectedPlant ? selectedPlant.entityId : null });

                }
                catch (error) {
                    if (error.response.status === 409) {
                        setDuplicateTitle(true);
                    }
                }
            }
        }
    }

    const handleReset = (event) => {
        setAlert({...newAlert, entityId: selectedPlant ? selectedPlant.entityId : null});
    }

    const handleSensChange = (event) => {
        setAlert({ ...alert, sensitivity: event.target.value });
    }

    const handleSenseBlur = () => {
        const newValue = parseInt(alert.sensitivity, 10);
        if (isNaN(newValue)) {
            setAlert({ ...alert, sensitivity: 5 });
        } else if (newValue < 5) {
            setAlert({ ...alert, sensitivity: 5 });
        } else if (newValue > 60) {
            setAlert({ ...alert, sensitivity: 60 });
        } else {
            setAlert({ ...alert, sensitivity: newValue });
        }
    }

    return (
        <Col gap={16} flex=".25">
            <Text fontSize="ty120">Alert Definition</Text>
            <View height={525} className='alert-definiton'>
                <form onSubmit={handleSubmit} onReset={handleReset}>
                    <Col gap={16}>
                        <Col gap={8}>
                            <Select onChange={e => switchAlertType(e)} value={alert.typeId === 100 ? alertTypes[0] : alertTypes[1]}>
                                {alertTypes.map((alertType, index) => (
                                    <option key={index} value={alertType}>{alertType}</option>
                                ))}
                            </Select>
                        </Col>
                        <Col gap={8}>
                            <Input type="text" onChange={e => setAlert({ ...alert, title: e.target.value })} placeholder="Alert Name" value={alert.title || ''} />
                            {duplicateTitle && <Text color="error100" fontSize="ty80">An alert with this title already exists</Text>}
                        </Col>
                        <Col gap={8}>
                            <Row gap={8} className="alert-def-input">
                                <Label>Days</Label>
                                <DaySelector setAlert={setAlert} alert={alert}></DaySelector>
                            </Row>
                        </Col>
                        <Col gap={8}>
                            <Row gap={8} className="alert-def-input">
                                <Label>Start Time</Label>
                                <TimeSelector
                                    onChange={setStart}
                                    time={[alert.startHour === 0 ? '00' : alert.startHour, alert.startMin === 0 ? '00' : alert.startMin].join(':')}>
                                </TimeSelector>
                            </Row>
                        </Col>
                        <Col gap={8}>
                            <Row gap={8} className="alert-def-input">
                                <Label>End Time</Label>
                                <TimeSelector
                                    onChange={setEnd}
                                    time={[alert.endHour === 0 ? '00' : alert.endHour, alert.endMin === 0 ? '00' : alert.endMin].join(':')}>
                                </TimeSelector>
                            </Row>
                        </Col>
                        <Col gap={8}>
                            <Row gap={8} className="alert-def-input number-input">
                                <Label>Sensitivity</Label>
                                <Input
                                    type="text"
                                    onChange={handleSensChange}
                                    onBlur={handleSenseBlur}
                                    placeholder="minutes"
                                    value={alert.sensitivity || ''}
                                />
                            </Row>
                        </Col>
                        {alert.typeId === 100
                            ? <BoundaryAlert setBoundaries={setBoundaries} boundaryAlert={alert.boundaryAlert}></BoundaryAlert>
                            : <OutputChangeAlert
                                outputChangeAlert={alert.outputChangeAlert}
                                setChange={setChange}
                                setChangeType={setChangeType}
                                setSampleType={setSampleType}
                                setSampleHours={setSampleHours}
                                setIgnore={setIgnore}>
                            </OutputChangeAlert>}
                        <Col gap={8}>
                            <Row gap={8} className="alert-def-input">
                                <Label>Direction of Change</Label>
                                <Select onChange={e => { setAlert({ ...alert, directionOfChange: e.target.value }) }} value={alert.directionOfChange}>
                                    {changeDirections.map((dir, index) => (
                                        <option key={index} value={dir}>{dir}</option>
                                    ))}
                                </Select>
                            </Row>
                        </Col>
                        <Row gap={4}>
                            <Input type="reset" />
                            <Input type="submit" />
                            {/* <Button>Plot</Button> */}
                        </Row>
                    </Col>
                </form>
            </View>
        </Col >
    )
}

export default AlertDefinition;